import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Task, TaskChanges, TaskDetail, TaskListQuery, TaskPerson, taskService, TASKS_CHANGED_EVENT } from "../../services/taskService";
import { apiErrorMessage, browserToday, labelsInUse, personName } from "./taskUtils";
import { TaskRights, Viewer, taskRights } from "./taskPermissions";

export interface Toast {
    message: string;
    severity?: "success" | "error" | "info" | "warning";
    action?: { label: string; onClick: () => void };
}

export interface TaskStore {
    tasks: Task[];
    today: string;
    loading: boolean;
    error: string | null;
    people: TaskPerson[];
    peopleById: Map<string, TaskPerson>;
    /** Approved team members who can be given tasks, me first. */
    assignable: TaskPerson[];
    labels: string[];
    meId: string | undefined;
    /** The signed-in staff member, for the rules in taskPermissions.ts. */
    viewer: Viewer;
    /** What the signed-in staff member may do with one task. */
    rightsFor: (task: Pick<Task, "created_by_user_id" | "assignee_user_id">) => TaskRights;
    reload: (silent?: boolean) => Promise<void>;
    /** Put a task (new or changed) into the list. */
    upsert: (task: Task | TaskDetail) => void;
    dropLocal: (taskId: string) => void;
    /** Tell badges and other task views that tasks changed (e.g. after a bulk edit). */
    announce: () => void;
    /** Change a task now on screen, then save; rolls back if the save fails. */
    patch: (task: Task, changes: TaskChanges) => Promise<TaskDetail | null>;
    toast: (t: Toast) => void;
    lastToast: Toast | null;
    clearToast: () => void;
}

const TaskStoreContext = createContext<TaskStore | null>(null);

/** Tells the header badges (useNotificationCounts) that tasks changed here. */
const announceChange = (source: string) => window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT, { detail: { source } }));
let nextStoreId = 0;

export const useTaskStore = (): TaskStore => {
    const ctx = useContext(TaskStoreContext);
    if (!ctx) throw new Error("useTaskStore must be used inside <TaskStoreProvider>");
    return ctx;
};

/** Optional variant for components that also live outside the Tasks page. */
export const useOptionalTaskStore = () => useContext(TaskStoreContext);

/** Apply changes to a task on screen before the server answers. */
export function applyLocally(task: Task, changes: TaskChanges): Task {
    const next: Task = { ...task, ...(changes as Partial<Task>) };
    if (changes.status && changes.status !== task.status) {
        if (changes.status === "DONE") next.completed_at = new Date().toISOString();
        else if (task.status === "DONE") next.completed_at = null;
    }
    if ("due_date" in changes && !changes.due_date) next.due_time = null;
    return next;
}

interface ProviderProps {
    children: React.ReactNode;
    /** Limit what's loaded (e.g. one order's tasks). Default: everything open + done in the last 14 days. */
    query?: TaskListQuery;
    /** Refresh while the page is open. */
    pollMs?: number;
}

export const TaskStoreProvider: React.FC<ProviderProps> = ({ children, query, pollMs = 60000 }) => {
    const { user } = useAuth();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [today, setToday] = useState(browserToday());
    const [people, setPeople] = useState<TaskPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastToast, setLastToast] = useState<Toast | null>(null);
    const queryKey = JSON.stringify(query || {});
    const queryRef = useRef(query);
    queryRef.current = query;
    // Saves in flight — a background refresh mustn't overwrite them.
    const pending = useRef(0);
    // Which store announced a change — each ignores its own.
    const storeId = useRef(`tasks-${++nextStoreId}`);

    const reload = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [list, team] = await Promise.all([taskService.list(queryRef.current), taskService.people()]);
            if (pending.current === 0) setTasks(list.tasks);
            setToday(list.today);
            setPeople(team);
            setError(null);
        } catch (err) {
            if (!silent) setError(apiErrorMessage(err, "Couldn't load tasks."));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload, queryKey]);

    useEffect(() => {
        if (!pollMs) return;
        const interval = setInterval(() => {
            if (document.visibilityState === "visible") reload(true);
        }, pollMs);
        const onFocus = () => reload(true);
        window.addEventListener("focus", onFocus);
        // A task added or changed elsewhere on this tab (e.g. from an order's
        // Tasks section or the header's "+ Task") shows up here too.
        let settle: ReturnType<typeof setTimeout> | undefined;
        const onChanged = (e: Event) => {
            if ((e as CustomEvent).detail?.source === storeId.current) return;
            clearTimeout(settle);
            settle = setTimeout(() => reload(true), 600);
        };
        window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
        return () => {
            clearInterval(interval);
            clearTimeout(settle);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
        };
    }, [pollMs, reload]);

    // Everyone's open/overdue counts (people strip, pickers) follow changes.
    const peopleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const refreshPeopleSoon = useCallback(() => {
        clearTimeout(peopleTimer.current);
        peopleTimer.current = setTimeout(() => {
            taskService.people().then(setPeople).catch(() => undefined);
        }, 800);
    }, []);
    useEffect(() => () => clearTimeout(peopleTimer.current), []);

    const upsert = useCallback((task: Task | TaskDetail) => {
        // Keep list rows lean — the drawer loads checklist/comments itself.
        const { checklist, comments, events, ...row } = task as TaskDetail;
        announceChange(storeId.current);
        refreshPeopleSoon();
        setTasks((prev) => {
            const i = prev.findIndex((t) => t.task_id === row.task_id);
            if (i === -1) return [row as Task, ...prev];
            const next = prev.slice();
            next[i] = row as Task;
            return next;
        });
    }, [refreshPeopleSoon]);

    const dropLocal = useCallback((taskId: string) => {
        announceChange(storeId.current);
        refreshPeopleSoon();
        setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
    }, [refreshPeopleSoon]);

    const toast = useCallback((t: Toast) => setLastToast(t), []);
    const clearToast = useCallback(() => setLastToast(null), []);

    const patch = useCallback(
        async (task: Task, changes: TaskChanges) => {
            upsert(applyLocally(task, changes));
            pending.current += 1;
            let saved: TaskDetail;
            try {
                saved = await taskService.update(task.task_id, changes);
            } catch (err) {
                pending.current -= 1;
                upsert(task);
                toast({ message: apiErrorMessage(err, "Couldn't save that change."), severity: "error" });
                return null;
            }
            pending.current -= 1;
            upsert(saved);
            // Finishing a repeating task creates its next copy on the server.
            const lastStatus = [...saved.events].reverse().find((e) => e.kind === "status");
            const rolled = changes.status === "DONE" && task.status !== "DONE" && !!lastStatus?.payload?.next_task_id;
            if (rolled) {
                await reload(true);
                toast({ message: "Done — the next one is on the list.", severity: "success" });
            }
            return saved;
        },
        [reload, toast, upsert],
    );

    const peopleById = useMemo(() => new Map(people.map((p) => [p.user_id, p])), [people]);
    const assignable = useMemo(() => {
        const list = people.filter((p) => p.assignable);
        list.sort((a, b) =>
            a.user_id === user?.user_id ? -1 : b.user_id === user?.user_id ? 1 : personName(a).localeCompare(personName(b)),
        );
        return list;
    }, [people, user?.user_id]);
    const labels = useMemo(() => labelsInUse(tasks), [tasks]);

    const viewer = useMemo<Viewer>(() => ({ userId: user?.user_id, role: user?.role }), [user?.user_id, user?.role]);
    const rightsFor = useCallback<TaskStore["rightsFor"]>((task) => taskRights(task, viewer), [viewer]);

    const value = useMemo<TaskStore>(
        () => ({
            tasks,
            today,
            loading,
            error,
            people,
            peopleById,
            assignable,
            labels,
            meId: user?.user_id,
            viewer,
            rightsFor,
            reload,
            upsert,
            dropLocal,
            announce: () => {
                announceChange(storeId.current);
                refreshPeopleSoon();
            },
            patch,
            toast,
            lastToast,
            clearToast,
        }),
        [tasks, today, loading, error, people, peopleById, assignable, labels, user?.user_id, viewer, rightsFor, reload, upsert, dropLocal, refreshPeopleSoon, patch, toast, lastToast, clearToast],
    );

    return <TaskStoreContext.Provider value={value}>{children}</TaskStoreContext.Provider>;
};
